import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react'
import { useTranslation } from '../../i18n'

/**
 * Shown when the AI rejected a video a person had approved: a banner, and the
 * reversed clips pinned above the grid so the moderator can see what happened
 * rather than take it on trust.
 *
 * These tiles are deliberately NOT part of `slots`. A slot flows through the
 * claiming protocol — it consumes grid capacity, it is claimed, and `onDecide`
 * releases it. An override is already moderated and nobody claims it, so
 * putting one in `slots` would corrupt `available`, `load` and the queue count,
 * which `../UPLOAD_TESTER/moderation-tester.mjs` asserts exactly. This is a
 * notification with video in it, not a work queue.
 */
function OverrideTile({ video, onAcknowledge }) {
  const { t } = useTranslation()
  return (
    <Box
      borderWidth="3px"
      borderColor="red.400"
      borderRadius="md"
      overflow="hidden"
      bg="black"
      minW="180px"
      maxW="180px"
      flexShrink={0}
    >
      <Box
        as="video"
        src={video.videoUrl}
        controls
        muted
        playsInline
        preload="metadata"
        w="100%"
        h="120px"
        objectFit="contain"
        bg="black"
      />
      <Stack gap={1} p={2}>
        <Text fontSize="xs" color="red.200" lineClamp={1}>
          {video.challengeLabel || video.challenge || video.id.slice(0, 8)}
        </Text>
        <Button size="xs" colorPalette="red" onClick={() => onAcknowledge(video.id)}>
          {t('moderation.aiOverrideAck')}
        </Button>
      </Stack>
    </Box>
  )
}

export function AiOverrideStrip({ overrides, onAcknowledge, onAcknowledgeAll }) {
  const { t } = useTranslation()
  if (!overrides || overrides.length === 0) return null

  return (
    <Box bg="red.900" borderWidth="2px" borderColor="red.400" borderRadius="md" p={3} mb={2}>
      <Flex align="center" justify="space-between" gap={4} wrap="wrap" mb={3}>
        <Stack gap={0}>
          <Text fontWeight="700" color="red.100">
            {t('moderation.aiOverrideTitle', { count: overrides.length })}
          </Text>
          <Text fontSize="sm" color="red.200">{t('moderation.aiOverrideBody')}</Text>
        </Stack>
        {overrides.length > 1 && (
          <Button size="xs" colorPalette="red" onClick={onAcknowledgeAll}>
            {t('moderation.aiOverrideAckAll')}
          </Button>
        )}
      </Flex>
      <Flex gap={2} overflowX="auto" pb={1}>
        {overrides.map((video) => (
          <OverrideTile key={video.id} video={video} onAcknowledge={onAcknowledge} />
        ))}
      </Flex>
    </Box>
  )
}
